import { type CoWorkerCalendarItem } from "@/features/calendar/types";
import {
  addDays,
  addMinutes,
  areIntervalsOverlapping,
  differenceInDays,
  format,
  isBefore,
} from "date-fns";

import {
  WEEK_BLOCK_HEIGHT,
  WEEK_DATE_TITLE_HEIGHT,
  WEEK_HOUR_TITLE_BLOCK_WIDTH,
} from "./constants";

const CANCELLED_STATUSES = new Set(["CANCELLED", "CANCELLED_NO_SHOW"]);

export const isCancelledCalendarItem = (
  item: CoWorkerCalendarItem
): boolean => {
  if (item.appointmentType === "outlook") {
    return Boolean(item.isCancelled);
  }

  if ("status" in item && item.status !== undefined) {
    return CANCELLED_STATUSES.has(item.status);
  }

  return false;
};

const getAllDayMapKey = (date: Date) => format(date, "yyyy-MM-dd");

export const populateAllDayMap = (items: CoWorkerCalendarItem[]) => {
  const dayMap: Record<string, number> = {};
  const itemMap: Record<string, number> = {};

  for (const item of items) {
    if (!item.id) {
      // eslint-disable-next-line no-console
      console.error("Item has not task ID, skipping.");
      continue;
    }

    let currDate = item.start;
    let maxIndex = 0;
    do {
      const key = getAllDayMapKey(currDate);
      dayMap[key] = (dayMap[key] ?? -1) + 1;
      currDate = addDays(currDate, 1);

      maxIndex = Math.max(maxIndex, dayMap[key]);
    } while (isBefore(currDate, addMinutes(item.end, 1)));

    itemMap[item.id] = maxIndex;
  }

  return itemMap;
};

export const getAllDayHeight = (
  items: CoWorkerCalendarItem[],
  height: number
) => {
  const dayMap = populateAllDayMap(items);

  let max = 1;
  Object.values(dayMap).forEach((value) => {
    if (value > max) {
      max = value;
    }
  });

  return (max + 1) * height + 5; // some space until start time
};

export const getTopPos = (
  allDayHeight: number,
  hourOffset: number,
  minuteOffset: number
) =>
  WEEK_DATE_TITLE_HEIGHT +
  allDayHeight +
  hourOffset * WEEK_BLOCK_HEIGHT +
  minuteOffset;

export const countOverlaps = (
  appointment: CoWorkerCalendarItem,
  appointments: CoWorkerCalendarItem[]
) => {
  let appointmentOverlapCount = 1;
  let appointmentOverlapIndex = undefined;
  for (const a of appointments) {
    if (a.id !== appointment.id) {
      if (
        areIntervalsOverlapping(
          { start: appointment.start, end: appointment.end },
          { start: a.start, end: a.end }
        )
      ) {
        appointmentOverlapCount++;
      }
    } else {
      if (appointmentOverlapIndex === undefined) {
        appointmentOverlapIndex = appointmentOverlapCount;
      }
    }
  }

  return {
    appointmentOverlapCount,
    appointmentOverlapIndex:
      appointmentOverlapIndex !== undefined ? appointmentOverlapIndex - 1 : 0,
  };
};

export interface LaidOutItem {
  item: CoWorkerCalendarItem;
  top: number;
  height: number;
  baseLeft: number;
  spanMinutes: number;
}

export interface LaneAssignmentResult {
  laneAssignments: number[];
  totalLanesForItem: number[];
}

/**
 * Assigns each block to a lane (horizontal layer) to prevent overlaps.
 *
 * Algorithm:
 * 1. Group items by day column (baseLeft)
 * 2. Within each column, sort by top position, then by duration (longer first)
 * 3. For each block, assign it to lane = (max lane of all preceding overlapping blocks) + 1
 * 4. Calculate total lanes for each block = max lane among all blocks that overlap it + 1
 *
 * This guarantees:
 * - No two overlapping blocks share the same lane
 * - Longer blocks claim earlier (leftmost) lanes
 * - Monotonic lane increase prevents unintended reuse
 */
export const assignLanes = (
  laidOutItems: LaidOutItem[]
): LaneAssignmentResult => {
  const laneAssignments: number[] = new Array(laidOutItems.length).fill(0);
  const totalLanesForItem: number[] = new Array(laidOutItems.length).fill(1);

  // Group original indices by day column
  const columnGroups = new Map<number, number[]>();
  for (let idx = 0; idx < laidOutItems.length; idx++) {
    const { baseLeft } = laidOutItems[idx];
    if (!columnGroups.has(baseLeft)) {
      columnGroups.set(baseLeft, []);
    }
    columnGroups.get(baseLeft)!.push(idx);
  }

  for (const columnIndices of columnGroups.values()) {
    // Sort by start position, then cancelled state, then longer spans.
    // Cancelled items are placed in lower lanes so they render underneath
    // non-cancelled items when blocks overlap.
    const sorted = [...columnIndices].sort(
      (a, b) =>
        laidOutItems[a].top - laidOutItems[b].top ||
        Number(isCancelledCalendarItem(laidOutItems[b].item)) -
          Number(isCancelledCalendarItem(laidOutItems[a].item)) ||
        laidOutItems[b].spanMinutes - laidOutItems[a].spanMinutes
    );

    for (let si = 0; si < sorted.length; si++) {
      const idx = sorted[si];
      const curr = laidOutItems[idx];
      let maxPrecedingLane = -1;

      // Look at all blocks that appear before this one (in top order) and overlap it
      for (let pi = 0; pi < si; pi++) {
        const prevIdx = sorted[pi];
        const prev = laidOutItems[prevIdx];
        const overlaps =
          curr.top < prev.top + prev.height &&
          prev.top < curr.top + curr.height;
        if (overlaps) {
          maxPrecedingLane = Math.max(
            maxPrecedingLane,
            laneAssignments[prevIdx]
          );
        }
      }

      laneAssignments[idx] = maxPrecedingLane + 1;
    }

    // Total lanes for each item = max lane among all items that visually overlap it + 1
    for (const idx of columnIndices) {
      const curr = laidOutItems[idx];
      let maxLane = laneAssignments[idx];
      for (const otherIdx of columnIndices) {
        if (idx !== otherIdx) {
          const comp = laidOutItems[otherIdx];
          const hasVisualOverlap =
            curr.top < comp.top + comp.height &&
            comp.top < curr.top + curr.height;
          if (hasVisualOverlap) {
            maxLane = Math.max(maxLane, laneAssignments[otherIdx]);
          }
        }
      }
      totalLanesForItem[idx] = maxLane + 1;
    }
  }

  return { laneAssignments, totalLanesForItem };
};

export const getLeftPos = (
  startDate: Date,
  itemStartDate: Date,
  blockWidth: number
) => {
  const dayOffset = differenceInDays(itemStartDate, startDate);
  return WEEK_HOUR_TITLE_BLOCK_WIDTH + dayOffset * blockWidth;
};

/**
 * Returns the maximum number of lanes needed across all day columns.
 * Can be used to size day columns when visual lane overlap is enabled.
 * Lane assignment is purely vertical — it does not depend on blockWidth.
 */
export const computeMaxLanes = (
  items: CoWorkerCalendarItem[],
  allDayHeight: number,
  calStartHour: number
): number => {
  if (items.length === 0) {
    return 1;
  }

  const baseStart = items.reduce(
    (min, i) => (i.start.getTime() < min.getTime() ? i.start : min),
    items[0].start
  );

  const laid = items.map((item) => {
    const displayStart = item.displayStart ?? item.start;
    const hourOffset = Math.max(displayStart.getHours() - calStartHour, 0);
    const minuteOffset = (displayStart.getMinutes() * WEEK_BLOCK_HEIGHT) / 60;
    const top =
      WEEK_DATE_TITLE_HEIGHT +
      allDayHeight +
      hourOffset * WEEK_BLOCK_HEIGHT +
      minuteOffset;

    // Match getBlockHeight's end-of-day cap to avoid overestimating overlaps.
    const endOfDay = new Date(displayStart);
    endOfDay.setHours(23, 59, 59, 999);
    const maxHeightMinutes = Math.floor(
      (endOfDay.getTime() - displayStart.getTime()) / 60000
    );
    const hours = Math.floor(maxHeightMinutes / 60); // align with grid lines
    const calculatedHeight =
      (Math.min(maxHeightMinutes + hours, item.length) * WEEK_BLOCK_HEIGHT) /
      60;

    const MIN_HEIGHT = 25;
    const height = Math.max(calculatedHeight, MIN_HEIGHT);
    const dayOffset = differenceInDays(displayStart, baseStart);
    return { top, height, dayOffset };
  });

  // Group by day
  const dayGroups = new Map<number, number[]>();
  for (let idx = 0; idx < laid.length; idx++) {
    const { dayOffset } = laid[idx];
    if (!dayGroups.has(dayOffset)) {
      dayGroups.set(dayOffset, []);
    }
    dayGroups.get(dayOffset)!.push(idx);
  }

  let globalMaxLanes = 1;

  for (const groupIndices of dayGroups.values()) {
    const sorted = [...groupIndices].sort((a, b) => laid[a].top - laid[b].top);
    const laneOf: number[] = new Array(groupIndices.length).fill(0);
    const sortedLaneOf: number[] = new Array(sorted.length).fill(0);

    for (let si = 0; si < sorted.length; si++) {
      const curr = laid[sorted[si]];
      let maxPreceding = -1;
      for (let pi = 0; pi < si; pi++) {
        const prev = laid[sorted[pi]];
        const overlaps =
          curr.top < prev.top + prev.height &&
          prev.top < curr.top + curr.height;
        if (overlaps) {
          maxPreceding = Math.max(maxPreceding, sortedLaneOf[pi]);
        }
      }
      sortedLaneOf[si] = maxPreceding + 1;
      laneOf[groupIndices.indexOf(sorted[si])] = sortedLaneOf[si];
    }

    // Max total lanes = highest lane among all items that overlap each item + 1
    for (let i = 0; i < groupIndices.length; i++) {
      const curr = laid[groupIndices[i]];
      let maxLane = laneOf[i];
      for (let j = 0; j < groupIndices.length; j++) {
        if (i !== j) {
          const comp = laid[groupIndices[j]];
          const overlaps =
            curr.top < comp.top + comp.height &&
            comp.top < curr.top + curr.height;
          if (overlaps) {
            maxLane = Math.max(maxLane, laneOf[j]);
          }
        }
      }
      globalMaxLanes = Math.max(globalMaxLanes, maxLane + 1);
    }
  }

  return globalMaxLanes;
};

export const getRightPos = (
  endDate: Date,
  itemStartDate: Date,
  blockWidth: number
) => {
  const dayOffset = differenceInDays(endDate, itemStartDate);

  return dayOffset * blockWidth;
};
